/**
 * Recipient Form JavaScript
 * Handles gift card recipient form functionality
 * Ported from TypeScript master to match exact functionality
 */

// Load GSAP helper (similar to side-cart.js and lightbox-gallery.js)
function loadGSAP() {
  return new Promise((resolve) => {
    if (window.gsap) {
      resolve(window.gsap);
      return;
    }

    // Load GSAP from CDN
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
    script.onload = () => resolve(window.gsap);
    script.onerror = () => {
      console.warn('GSAP failed to load, using fallback animations');
      resolve(null);
    };
    document.head.appendChild(script);
  });
}

class RecipientForm extends HTMLElement {
  constructor() {
    super();
    this.gsap = null;
    
    // Get elements using IDs (matching TypeScript master selectors)
    this.recipientFieldsLiveRegion = this.querySelector('#recipient-fields-live-region');
    this.checkboxInput = this.querySelector('#recipient-checkbox');
    this.hiddenControlField = this.querySelector('#recipient-control');
    this.form = this.querySelector('.recipientForm__fields');
    this.inputFields = this.querySelectorAll('.recipientForm__fieldInput');
    
    this.onChange = this.onChange.bind(this);
  }

  async connectedCallback() {
    // Load GSAP
    this.gsap = await loadGSAP();
    
    // Bind events
    this.bindEvents();
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.checkboxInput) {
      this.checkboxInput.removeEventListener('change', this.onChange);
    }
  }

  bindEvents() {
    if (this.checkboxInput) {
      this.checkboxInput.addEventListener('change', this.onChange);
    }
  }

  onChange() {
    if (this.checkboxInput.checked) {
      this.enableInputFields();
      this.expandForm();
      if (this.recipientFieldsLiveRegion) {
        this.recipientFieldsLiveRegion.innerText = 'Recipient form expanded';
      }
    } else {
      this.collapseForm();
      this.clearInputFields();
      this.disableInputFields();
      if (this.recipientFieldsLiveRegion) {
        this.recipientFieldsLiveRegion.innerText = 'Recipient form collapsed';
      }
    }
  }

  expandForm() {
    if (!this.form) return;
    
    this.form.setAttribute('aria-hidden', 'false');

    if (this.gsap) {
      // Use GSAP animations (matches TypeScript master)
      this.gsap.killTweensOf(this.form);
      this.gsap.set(this.form, { pointerEvents: 'all' });
      this.gsap.to(this.form, { height: 'auto', duration: 0.5, ease: 'power4.out' });
    } else {
      // Fallback to CSS transitions if GSAP not available
      this.form.style.pointerEvents = 'all';
      this.form.style.height = 'auto';
    }
  }

  collapseForm() {
    if (!this.form) return;
    
    this.form.setAttribute('aria-hidden', 'true');

    if (this.gsap) {
      // Use GSAP animations (matches TypeScript master)
      this.gsap.killTweensOf(this.form);
      this.gsap.to(this.form, { height: 0, duration: 0.5, ease: 'power4.out' });
    } else {
      // Fallback to CSS transitions if GSAP not available
      this.form.style.height = '0';
    }
  }

  enableInputFields() {
    if (this.hiddenControlField) {
      this.hiddenControlField.disabled = false;
    }

    this.inputFields.forEach((field) => {
      const fieldName = field.getAttribute('name');

      field.disabled = false;

      if (fieldName === 'properties[Recipient email]') {
        field.required = true;
      }
    });
  }

  disableInputFields() {
    if (this.hiddenControlField) {
      this.hiddenControlField.disabled = true;
    }

    this.inputFields.forEach((field) => {
      const fieldName = field.getAttribute('name');

      field.disabled = true;

      if (fieldName === 'properties[Recipient email]') {
        field.required = false;
      }
    });
  }

  clearInputFields() {
    this.inputFields.forEach((field) => {
      field.value = '';
    });
  }
}

// Register the custom element
if (!customElements.get('recipient-form')) {
  customElements.define('recipient-form', RecipientForm);
}

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.RecipientForm = RecipientForm;
}

